import React, { useMemo } from 'react';

import {
  FileTextOutlined,
  CheckCircleOutlined,
  EuroCircleOutlined,
  DropboxOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Row, Timeline, Col, Tooltip, Flex, Typography } from 'antd';
import { parseISO } from 'date-fns';
import styled from 'styled-components';

import EventsTable from './EventsTable';
import useGetStateEventHistoryShipment from '../hooks/useGetStateEventHistoryShipment';
import { ShipmentEventName } from '../types/shipmenteventName';
import { ShipmentEventType } from '../types/shipmentEventTypes';
import InspectionIcon from '@/components/icons/InspectionIcon';
import { LitigationIcon } from '@/components/icons/LitigationIcon';
import InlineSpacer from '@/components/layout/InlineSpacer';
import dates from '@/helpers/dates';

const { Item } = Timeline;

const Wrapper = styled.div`
  display: flex;
  flex-flow: column;
`;

const WrapperTimeline = styled.div`
  height: 40vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 1.5rem;
`;

const chooseIcon = (eventType, eventName = '') => {
  switch (eventType) {
    case ShipmentEventType.DELIVERY_STATUS:
      return <DropboxOutlined style={{ color: 'brown' }} />;
    case ShipmentEventType.CONTRACT:
      if (eventName === ShipmentEventName.INSPECTION_END) {
        return <CheckCircleOutlined />;
      }
      if (eventName === ShipmentEventName.INSPECTION_START) {
        return <InspectionIcon />;
      }

      return <FileTextOutlined style={{ color: 'blue' }} />;
    case ShipmentEventType.CONFIRMATION:
      return <CheckCircleOutlined style={{ color: 'green' }} />;
    case ShipmentEventType.PAYMENT:
      return <EuroCircleOutlined style={{ color: 'black' }} />;
    case ShipmentEventType.LITIGATION:
      return (
        <LitigationIcon
          style={{
            color: '#B73DB4',
            width: '1rem',
            height: '1rem',
          }}
        />
      );
  }
};

const litigationTitle = (eventName) => {
  switch (eventName) {
    case ShipmentEventName.FLAGGED:
      return (
        <Typography style={{ fontSize: '.875rem', fontWeight: 500 }}>
          Shipment in litigation
        </Typography>
      );
    case ShipmentEventName.SOLVED:
      return (
        <Typography style={{ fontSize: '.875rem', fontWeight: 500 }}>
          Litigation solved
        </Typography>
      );
    case ShipmentEventName.CANCELED:
      return (
        <Typography style={{ fontSize: '.875rem', fontWeight: 500 }}>
          Litigation canceled
        </Typography>
      );
  }
};

const ShipmentStateEventHistory = ({ id }) => {
  const events = useGetStateEventHistoryShipment(id);

  const Items = useMemo(() => {
    if (events.length) {
      return events.map((e, i) => {
        const isIinspectionEvent =
          e.eventName === ShipmentEventName.INSPECTION_END ||
          e.eventName === ShipmentEventName.INSPECTION_START;
        const icon =
          e.eventCause === 'BOX' && e.boxEventTime === null ? (
            <Tooltip title="The boxTimestamp is not available, so the backend consumeAt is used instead.">
              <WarningOutlined style={{ color: 'orange' }} />
            </Tooltip>
          ) : (
            chooseIcon(e.eventType, e.eventName)
          );

        const dateEvent =
          e.eventCause === 'BOX' &&
          e.boxEventTime !== null &&
          e.boxEventTime !== undefined
            ? dates.formatDate(parseISO(e.boxEventTime))
            : dates.parseDate(e.createdAt);

        return (
          <Item
            key={e.id}
            dot={icon}
            label={dateEvent}
            style={{ marginBottom: '0.75rem', marginTop: '0.75rem' }}
          >
            <div style={i === 0 ? { fontSize: '20px' } : { fontSize: '15px' }}>
              {e.eventType === ShipmentEventType.LITIGATION
                ? litigationTitle(e.eventName)
                : e.eventName}
            </div>
            {e.eventCause &&
              e.eventType !== ShipmentEventType.LITIGATION &&
              !isIinspectionEvent && (
                <Flex gap="0.2rem">
                  <Typography>cause:</Typography>
                  {e.eventCause}
                </Flex>
              )}
            {e?.payload?.reason && (
              <Flex gap="0.2rem">
                <Typography>reason:</Typography>
                {e.payload.reason
                  .replace(/([a-z])([A-Z])/g, '$1 $2')
                  .replace(/^./, (str) => str.toUpperCase())}
              </Flex>
            )}
            {e?.payload?.details && (
              <Flex
                gap="0.2rem"
                style={{
                  flexWrap: 'wrap',
                }}
              >
                <Typography>comments:</Typography>
                {e.payload.details}
              </Flex>
            )}
            {e.notes && (
              <Flex gap="0.2rem">
                <Typography>notes:</Typography>
                {e.notes}
              </Flex>
            )}
            <Row>
              {e.eventName === ShipmentEventName.INSPECTION_END &&
              e?.payload?.note ? (
                <>
                  <Col>
                    {' '}
                    <Typography
                      style={{
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontWeight: 400,
                        color: '#595959',
                      }}
                    >
                      By: {e?.payload?.user_name}
                    </Typography>
                  </Col>
                  <Col>
                    <Typography
                      style={{
                        fontSize: '10px',
                        lineHeight: '140%',
                        fontWeight: 400,
                        color: '#595959',
                      }}
                    >
                      {e?.payload?.note}
                    </Typography>{' '}
                  </Col>
                </>
              ) : null}
            </Row>
          </Item>
        );
      });
    }

    return <p>No events.</p>;
  }, [events]);

  const Legend = useMemo(() => {
    if (events.length) {
      return (
        <Flex align="center" style={{ marginBottom: '2rem' }}>
          {chooseIcon('DELIVERY_STATUS')} <InlineSpacer width="0.7rem" />
          DELIVERY_STATUS <InlineSpacer width="2rem" />
          {chooseIcon('CONTRACT')} <InlineSpacer width="0.7rem" /> CONTRACT
          <InlineSpacer width="2rem" />
          {chooseIcon('CONFIRMATION')} <InlineSpacer width="0.7rem" />
          CONFIRMATION <InlineSpacer width="2rem" />
          {chooseIcon('PAYMENT')} <InlineSpacer width="0.7rem" /> PAYMENT
          <InlineSpacer width="2rem" />
          {chooseIcon('LITIGATION')} <InlineSpacer width="0.7rem" /> SHIPMENT IN
          LITIGATION
          <InlineSpacer width="2rem" />
        </Flex>
      );
    }
  }, [events.length]);

  return (
    <Wrapper>
      {Legend}
      <Row>
        <Col flex="18">
          <WrapperTimeline>
            <Timeline mode="left">{Items}</Timeline>
          </WrapperTimeline>
        </Col>

        <Col flex="24">
          <EventsTable id={id} />
        </Col>
      </Row>
    </Wrapper>
  );
};
export default ShipmentStateEventHistory;
